import { Box, IconButton, Typography, Collapse } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useEffect, useRef, useState } from "react";
import NoItemMessage from "app/main/apps/common/NoItemMessage";
import AuraToolbarButton from "app/main/apps/common/AuraToolbarButton";

const SPY_OFFSET_PX = 24;
const SCROLL_UNLOCK_DELTA_PX = 56;
/** Outlasts MUI Collapse timeout="auto" for tall cards (~561ms at 2000px). */
const ALIGN_LONG_STOP_MS = 800;

/** An element only counts as the scroll root if it can actually scroll — content taller than its box. */
const isScrollable = (el) => !!el && el.scrollHeight > el.clientHeight + 1;

/** Exported for test: picking the wrong root is silent — scrollTop on a non-scroller throws nothing. */
export const getScrollParent = (node) => {
  if (!node) {
    return document.scrollingElement || document.documentElement;
  }

  // Order pages scroll #fuse-main (see AuraPageCarded GlobalStyles).
  //
  // PGSS-5209: only when it is actually the element that scrolls. This shortcut used to return #fuse-main
  // on a bare contains() check, skipping the scrollHeight test the generic search below applies to every
  // other candidate. On a tab whose content scrolls in a nested container, #fuse-main is present and
  // contains the card but never overflows, so `scrollRoot.scrollTop += delta` moved nothing and threw no
  // error. The nav row highlighted (setActiveId is independent) and the page stayed put — reported as
  // "selecting a revision highlights but does not scroll".
  const fuseMain = document.getElementById("fuse-main");
  const onFusePage = !!fuseMain && fuseMain.contains(node);
  if (onFusePage && isScrollable(fuseMain)) {
    return fuseMain;
  }

  let el = node.parentElement;
  let overflowFallback = null;
  while (el && el !== document.body) {
    const style = window.getComputedStyle(el);
    const overflowY = style.overflowY;
    if (
      overflowY === "auto" ||
      overflowY === "scroll" ||
      overflowY === "overlay"
    ) {
      overflowFallback = overflowFallback || el;
      if (isScrollable(el)) {
        return el;
      }
    }
    el = el.parentElement;
  }
  // PGSS-5209 (second re-test): #fuse-main outranks the document when nothing overflows yet.
  //
  // The scrollHeight test above is right, but it left this case with nowhere to go. On the Revision
  // History tab the cards start collapsed and sit straight in #fuse-main's content wrapper — no
  // overflow-auto ancestor, nothing tall enough to overflow — so the walk found no candidate and fell
  // through to document.scrollingElement. On a Fuse layout that element never scrolls: body is fixed and
  // #fuse-main is the scroller. So the root was resolved, once, to the one element guaranteed not to
  // move, and every later click scrolled it. Same silent failure and the same symptom as before, arrived
  // at from the opposite direction.
  //
  // A declared overflow-auto ancestor still wins even when it is not overflowing yet — it is what will
  // scroll once a Collapse expands, and it is nearer the card than #fuse-main.
  return (
    overflowFallback ||
    (onFusePage ? fuseMain : null) ||
    document.scrollingElement ||
    document.documentElement
  );
};

const isDocumentScrollRoot = (scrollRoot) =>
  scrollRoot === document.scrollingElement ||
  scrollRoot === document.documentElement ||
  scrollRoot === document.body;

/** Card whose top has crossed the spy line (near top of scroll viewport). */
const findActiveId = (orderedIds, cardRefs, scrollRoot, offsetPx) => {
  if (!orderedIds.length) return null;
  const rootTop = isDocumentScrollRoot(scrollRoot)
    ? 0
    : scrollRoot.getBoundingClientRect().top;
  const spyY = rootTop + offsetPx;

  let active = orderedIds[0];
  for (const id of orderedIds) {
    const node = cardRefs.current[id];
    if (!node) continue;
    if (node.getBoundingClientRect().top <= spyY + 4) {
      active = id;
    } else {
      break;
    }
  }
  return active;
};

/**
 * Generic 2c master-detail shell: sticky left index + scroll-spy synced stacked
 * cards, with Expand all / Collapse all / Focus mode.
 *
 * Render props keep this ignorant of what a "revision" contains, so both
 * Revision Histories and Change History can share the exact same interaction.
 */
const HistorySplitView = ({
  items,
  getKey,
  getInitialExpanded = () => false,
  renderNavRow,
  renderHeader,
  renderBody,
  navHeading = "Revisions",
  emptyMessage = "No record found!",
  showFocusMode = true,
  /** When false, cards use full width (no sticky left index). */
  showNav = true,
  /** Optional control to toggle the left index on/off (e.g. List button). */
  showNavToggle = false,
  toolbarStart = null,
  toolbarEnd = null,
  testIds = {},
}) => {
  const cardRefs = useRef({});
  const scrollRootRef = useRef(null);
  const pinnedIdRef = useRef(null);
  const pinnedScrollTopRef = useRef(null);
  const programmaticScrollRef = useRef(false);
  const alignTimersRef = useRef([]);
  const [activeId, setActiveId] = useState(null);
  const [expandedMap, setExpandedMap] = useState({});
  const [focusMode, setFocusMode] = useState(false);
  const [navEnabled, setNavEnabled] = useState(!!showNav);

  useEffect(() => {
    setNavEnabled(!!showNav);
  }, [showNav]);

  const orderedIds = (items || []).map((item, index) => getKey(item, index));
  const listIdentity = orderedIds.join("|");

  const clearAlignTimers = () => {
    alignTimersRef.current.forEach((t) => clearTimeout(t));
    alignTimersRef.current = [];
  };

  useEffect(() => {
    pinnedIdRef.current = null;
    pinnedScrollTopRef.current = null;
    programmaticScrollRef.current = false;
    clearAlignTimers();
    const next = {};
    (items || []).forEach((item, index) => {
      next[getKey(item, index)] = getInitialExpanded(item, index);
    });
    setExpandedMap(next);
    setActiveId(orderedIds[0] || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listIdentity]);

  useEffect(() => {
    const firstNode = orderedIds.map((id) => cardRefs.current[id]).find(Boolean);
    if (!firstNode) return undefined;

    const scrollRoot = getScrollParent(firstNode);
    scrollRootRef.current = scrollRoot;

    const syncActiveFromScroll = () => {
      if (programmaticScrollRef.current) return;

      const pinnedId = pinnedIdRef.current;
      if (pinnedId != null && pinnedScrollTopRef.current != null) {
        const moved = Math.abs(scrollRoot.scrollTop - pinnedScrollTopRef.current);
        if (moved < SCROLL_UNLOCK_DELTA_PX) {
          setActiveId((prev) => (prev === pinnedId ? prev : pinnedId));
          return;
        }
        pinnedIdRef.current = null;
        pinnedScrollTopRef.current = null;
      }

      const next = findActiveId(orderedIds, cardRefs, scrollRoot, SPY_OFFSET_PX);
      if (next) setActiveId((prev) => (prev === next ? prev : next));
    };

    scrollRoot.addEventListener("scroll", syncActiveFromScroll, {
      passive: true,
    });
    return () => {
      scrollRoot.removeEventListener("scroll", syncActiveFromScroll);
      clearAlignTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listIdentity]);

  const finishProgrammaticScroll = (id) => {
    const scrollRoot = scrollRootRef.current;
    programmaticScrollRef.current = false;
    pinnedIdRef.current = id;
    pinnedScrollTopRef.current = scrollRoot?.scrollTop ?? null;
    setActiveId(id);
  };

  const alignAfterLayout = (id, { finish = true } = {}) => {
    const target = cardRefs.current[id];
    // PGSS-5209: resolve the root now, not once at mount.
    //
    // Which element scrolls is not a fixed property of the page — it depends on whether anything is tall
    // enough to overflow, and at mount nothing is: every card is collapsed. The root cached then was
    // whatever the page looked like at its shortest. By the time this runs the target has expanded and the
    // real scroller is overflowing, so asking again gets the right answer. The cached value is kept only
    // as the fallback, and only for the scroll listener, which has to bind to something at mount.
    const scrollRoot = target
      ? getScrollParent(target)
      : scrollRootRef.current;
    if (target && scrollRoot) {
      scrollRootRef.current = scrollRoot;
      const rootTop = isDocumentScrollRoot(scrollRoot)
        ? 0
        : scrollRoot.getBoundingClientRect().top;
      const delta = target.getBoundingClientRect().top - rootTop - SPY_OFFSET_PX;
      if (Math.abs(delta) >= 2) {
        const before = scrollRoot.scrollTop;
        scrollRoot.scrollTop += delta;
        // Setting scrollTop on an element that cannot scroll is not an error — it silently does nothing,
        // which is how this bug survived two fixes. If the root did not move, ask the browser to bring the
        // card into view instead: it walks every scrollable ancestor itself and needs no guess from us.
        if (scrollRoot.scrollTop === before && target.scrollIntoView) {
          target.scrollIntoView({ block: "start", inline: "nearest" });
        }
      }
    }
    if (finish) finishProgrammaticScroll(id);
  };

  /** Re-align while Collapse height settles. Finish on onEntered; long-stop if no enter. */
  const scheduleAlign = (id) => {
    clearAlignTimers();
    programmaticScrollRef.current = true;
    requestAnimationFrame(() => alignAfterLayout(id, { finish: false }));
    [50, 200, 400].forEach((ms) => {
      alignTimersRef.current.push(
        setTimeout(() => alignAfterLayout(id, { finish: false }), ms)
      );
    });
    // Already-expanded nav clicks never fire Collapse onEntered — finish here.
    alignTimersRef.current.push(
      setTimeout(() => {
        if (programmaticScrollRef.current && pinnedIdRef.current === id) {
          alignAfterLayout(id, { finish: true });
        }
      }, ALIGN_LONG_STOP_MS)
    );
  };

  const scrollTo = (id) => {
    programmaticScrollRef.current = true;
    pinnedIdRef.current = id;
    pinnedScrollTopRef.current = null;
    setActiveId(id);
    setExpandedMap((prev) =>
      focusMode ? { [id]: true } : { ...prev, [id]: true }
    );
    scheduleAlign(id);
  };

  const toggle = (id) => {
    let nextOpen = false;
    programmaticScrollRef.current = true;
    pinnedIdRef.current = id;
    setActiveId(id);
    setExpandedMap((prev) => {
      nextOpen = !prev[id];
      if (nextOpen && focusMode) return { [id]: true };
      return { ...prev, [id]: nextOpen };
    });
    if (nextOpen) scheduleAlign(id);
    else {
      clearAlignTimers();
      finishProgrammaticScroll(id);
    }
  };

  const setAllExpanded = (open) => {
    clearAlignTimers();
    pinnedIdRef.current = null;
    pinnedScrollTopRef.current = null;
    const next = {};
    orderedIds.forEach((id) => {
      next[id] = open;
    });
    setExpandedMap(next);
    if (open) setFocusMode(false);
  };

  const allExpanded =
    orderedIds.length > 0 && orderedIds.every((id) => expandedMap[id]);
  const allCollapsed = orderedIds.every((id) => !expandedMap[id]);
  const hasItems = !!items?.length;

  return (
    <>
      {/* Single toolbar row: segment/context on the left, actions + view switch on the right. */}
      <div className="flex flex-wrap items-center justify-between gap-8 mb-12">
        <div className="flex flex-wrap items-center gap-8">{toolbarStart}</div>
        <div className="flex flex-wrap items-center gap-8">
          {showNavToggle && (
            <AuraToolbarButton
              onClick={() => setNavEnabled((prev) => !prev)}
              selected={navEnabled}
              disabled={!hasItems}
              dataTestId={testIds.navToggle || "history-nav-toggle"}
              title={
                navEnabled
                  ? "Hide revision list sidebar"
                  : "Show revision list sidebar"
              }
            >
              List
            </AuraToolbarButton>
          )}
          <AuraToolbarButton
            onClick={() => setAllExpanded(true)}
            disabled={!hasItems || allExpanded}
            dataTestId={testIds.expandAll}
          >
            Expand all
          </AuraToolbarButton>
          <AuraToolbarButton
            onClick={() => setAllExpanded(false)}
            disabled={!hasItems || allCollapsed}
            dataTestId={testIds.collapseAll}
          >
            Collapse all
          </AuraToolbarButton>
          {showFocusMode && (
            <AuraToolbarButton
              onClick={() => {
                const nextFocus = !focusMode;
                setFocusMode(nextFocus);
                if (nextFocus && activeId) setExpandedMap({ [activeId]: true });
              }}
              selected={focusMode}
              disabled={!hasItems}
              dataTestId={testIds.focus}
              title="Keep only the selected revision open"
            >
              Focus mode
            </AuraToolbarButton>
          )}
          {toolbarEnd && (
            <div
              className="flex flex-wrap items-center gap-8 pl-8"
              style={{ borderLeft: "1px solid #e6e9ef" }}
            >
              {toolbarEnd}
            </div>
          )}
        </div>
      </div>

      {!hasItems && (
        <Box data-testid={testIds.empty}>
          <NoItemMessage
            message={emptyMessage}
            style={{ message: "text-20" }}
          />
        </Box>
      )}

      {hasItems && (
      <div
        className={
          navEnabled
            ? // Avoid overflow-hidden here — it breaks position:sticky on the nav.
              "grid grid-cols-1 md:grid-cols-[220px_1fr] border border-solid border-[#e0e4ec] rounded-xl bg-white"
            : "flex flex-col gap-16"
        }
        data-testid={testIds.container}
      >
        {navEnabled && (
          <nav
            className="bg-[#fbfcfe] border-0 border-r border-solid border-[#eef1f5] rounded-tl-xl rounded-bl-xl md:sticky md:top-16 md:self-start md:max-h-[calc(100vh-120px)] md:overflow-auto z-10"
            data-testid={testIds.list}
            aria-label={`${navHeading} index`}
          >
            <div className="px-14 py-12 text-[11px] font-extrabold uppercase tracking-wider text-[#8a93a5] border-0 border-b border-solid border-[#eef1f5]">
              {navHeading}
            </div>
            {items.map((item, index) => {
              const id = getKey(item, index);
              const active = activeId === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => scrollTo(id)}
                  data-testid={testIds.navItem ? testIds.navItem(id) : undefined}
                  className="w-full text-left px-14 py-12 border-0 border-b border-solid border-[#f4f6f9] cursor-pointer"
                  style={{
                    background: active ? "#eef0ff" : "transparent",
                    borderLeft: active
                      ? "3px solid #4f46e5"
                      : "3px solid transparent",
                  }}
                >
                  {renderNavRow(item, index, { active })}
                </button>
              );
            })}
          </nav>
        )}

        <div
          className={
            navEnabled
              ? "px-16 py-16 flex flex-col gap-16 rounded-tr-xl rounded-br-xl min-w-0"
              : "flex flex-col gap-16"
          }
        >
          {items.map((item, index) => {
            const id = getKey(item, index);
            const expanded = !!expandedMap[id];
            const active = activeId === id;
            return (
              <div
                key={id}
                ref={(el) => {
                  if (el) cardRefs.current[id] = el;
                  else delete cardRefs.current[id];
                }}
                data-history-id={id}
                data-testid={testIds.card ? testIds.card(id) : undefined}
                className="border border-solid rounded-xl overflow-hidden"
                style={{
                  borderColor: active ? "#c7cdfa" : "#e6e9ef",
                  boxShadow: active ? "0 0 0 1px #c7cdfa" : "none",
                  scrollMarginTop: SPY_OFFSET_PX,
                }}
              >
                <div
                  className="flex items-start justify-between gap-12 px-16 py-12 bg-[#f7f9fc]"
                  style={{
                    borderBottom: expanded ? "1px solid #e6e9ef" : "none",
                  }}
                >
                  <div className="min-w-0 flex-1">
                    {renderHeader(item, index, { expanded })}
                  </div>
                  <IconButton
                    size="small"
                    onClick={() => toggle(id)}
                    title={expanded ? "Collapse" : "Expand"}
                    data-testid={testIds.toggle ? testIds.toggle(id) : undefined}
                    sx={{
                      border: "1px solid #d7dbe3",
                      borderRadius: "8px",
                      width: 30,
                      height: 30,
                      flexShrink: 0,
                      mt: "2px",
                    }}
                  >
                    {expanded ? (
                      <KeyboardArrowUpIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowDownIcon fontSize="small" />
                    )}
                  </IconButton>
                </div>

                <Collapse
                  in={expanded}
                  timeout="auto"
                  unmountOnExit={false}
                  onEntered={() => {
                    if (pinnedIdRef.current !== id) return;
                    // Layout settled — finish here so tall cards aren't cut off at 400ms.
                    clearAlignTimers();
                    alignAfterLayout(id, { finish: true });
                  }}
                >
                  <div>{renderBody(item, index)}</div>
                </Collapse>
              </div>
            );
          })}
        </div>
      </div>
      )}
    </>
  );
};

export default HistorySplitView;
